// 角色在线状态缓存管理器
// 缓存时长：1天

export interface CharacterOnlineStatus {
  characterId: string;
  isOnline: boolean;
  checkTime: number;
  expireTime: number;
}

const STATUS_KEY = 'character_online_status_v1';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 1天

class CharacterOnlineStatusManager {
  private cache: Map<string, CharacterOnlineStatus>;

  constructor() {
    this.cache = new Map();
    this.loadFromStorage();
  }

  /**
   * 从localStorage加载缓存
   */
  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STATUS_KEY);
      if (!raw) return;

      const data = JSON.parse(raw);
      const now = Date.now();

      // 过滤过期的缓存
      Object.entries(data).forEach(([charId, status]: [string, any]) => {
        if (status.expireTime > now) {
          this.cache.set(charId, status);
        }
      });

      console.log(`📦 已加载角色在线状态缓存，共 ${this.cache.size} 条记录`);
    } catch (e) {
      console.error('加载角色在线状态缓存失败:', e);
    }
  }

  /**
   * 保存缓存到localStorage
   */
  private saveToStorage(): void {
    try {
      const data: Record<string, CharacterOnlineStatus> = {};
      this.cache.forEach((status, charId) => {
        data[charId] = status;
      });
      localStorage.setItem(STATUS_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('保存角色在线状态缓存失败:', e);
    }
  }

  /**
   * 检查角色在线状态
   * @param characterId 角色ID
   * @returns 是否在线，null表示未知（需要查询）
   */
  getStatus(characterId: string): boolean | null {
    const cached = this.cache.get(characterId);
    if (!cached) return null;

    const now = Date.now();
    if (now > cached.expireTime) {
      // 缓存过期，删除记录
      this.cache.delete(characterId);
      this.saveToStorage();
      return null;
    }

    return cached.isOnline;
  }

  /**
   * 更新角色在线状态
   * @param characterId 角色ID
   * @param isOnline 是否在线
   */
  setStatus(characterId: string, isOnline: boolean): void {
    const now = Date.now();
    const status: CharacterOnlineStatus = {
      characterId,
      isOnline,
      checkTime: now,
      expireTime: now + CACHE_DURATION
    };

    this.cache.set(characterId, status);
    this.saveToStorage();

    if (import.meta.env.DEV) {
      console.log(`🔄 已更新角色 ${characterId} 在线状态: ${isOnline ? '在线' : '离线'}`);
    }
  }

  /**
   * 批量更新角色在线状态
   * @param statuses 角色状态映射
   */
  setStatuses(statuses: Record<string, boolean>): void {
    const now = Date.now();
    Object.entries(statuses).forEach(([charId, isOnline]) => {
      const status: CharacterOnlineStatus = {
        characterId: charId,
        isOnline,
        checkTime: now,
        expireTime: now + CACHE_DURATION
      };
      this.cache.set(charId, status);
    });
    this.saveToStorage();
  }

  /**
   * 清理过期的缓存
   */
  cleanupExpiredCache(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    this.cache.forEach((status, charId) => {
      if (now > status.expireTime) {
        expiredKeys.push(charId);
      }
    });

    expiredKeys.forEach(charId => {
      this.cache.delete(charId);
    });

    if (expiredKeys.length > 0) {
      this.saveToStorage();
      if (import.meta.env.DEV) {
        console.log(`🧹 已清理 ${expiredKeys.length} 条过期的角色状态缓存`);
      }
    }
  }

  /**
   * 获取所有缓存的角色状态
   */
  getAllStatuses(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    this.cache.forEach((status, charId) => {
      result[charId] = status.isOnline;
    });
    return result;
  }

  /**
   * 清除所有缓存
   */
  clearAll(): void {
    this.cache.clear();
    this.saveToStorage();
    console.log('🗑️ 已清除所有角色在线状态缓存');
  }

  /**
   * 获取缓存统计信息
   */
  getStats() {
    const now = Date.now();
    let onlineCount = 0;
    let offlineCount = 0;
    let expiredCount = 0;

    this.cache.forEach(status => {
      if (now > status.expireTime) {
        expiredCount++;
      } else if (status.isOnline) {
        onlineCount++;
      } else {
        offlineCount++;
      }
    });

    return {
      total: this.cache.size,
      online: onlineCount,
      offline: offlineCount,
      expired: expiredCount
    };
  }
}

// 导出单例实例
export const characterOnlineStatus = new CharacterOnlineStatusManager();

// 定期清理过期缓存（每小时）
if (typeof window !== 'undefined') {
  setInterval(() => {
    characterOnlineStatus.cleanupExpiredCache();
  }, 60 * 60 * 1000);
}
